import React, { useState, useRef } from 'react';
import { Cropper } from 'react-cropper';
import { css, StyleSheet } from 'aphrodite/no-important';
import { Drawer, FileUploader, Button, Loader } from 'quantux-ui';
import { gray, salmon, blue } from '../../theme';
import { BASE_URL } from '../../utils';

const CropperDrawer = (props) => {
  const [showCropper, setShowCropper] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [isImageUploading, setIsImageUploading] = useState(false);

  const {
    isVisible,
    placement,
    cropperAspectRatio = 1,
    headerText = 'Upload Patient Avatar',
    uploadUserMessage = 'Uploading Patient Avatar Image',
    cropButtonText = 'Crop Avatar Image',
    rawFileUploadUrl = 'users/uploadImg',
    croppedFileUploadUrl = 'patients/updatePatientAvatar',
    imageUploadFormData = {},
    style,
    zIndex = '2000',
    onClose,
    onCroppedImageUpload
  } = props;

  const cropperRef = useRef();
  const fileInputRef = useRef();

  // ------------------- Styles -------------------
  const styles = StyleSheet.create({
    sideDrawerContainer: {
      // background: gray[600],
      background: '#002448',
      color: gray[300],
      height: '100%',
      position: 'relative',
      display: 'flex',
      flexFlow: 'column'
    },
    sideDrawerHeader: {
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 8px',
      fontWeight: 'bold',
      borderBottom: '1px solid black',
      flexShrink: '0'
    },
    sideDrawerHeaderCloseIcon: {
      color: gray[300],
      ':hover': {
        color: salmon[500]
      }
    },
    sideDrawerBanner: {
      position: 'relative',
      padding: '8px',
      flexShrink: '0'
    },
    sideDrawerBannerImg: {
      width: '100%',
      height: 'auto'
    },
    sideDrawerBannerCameraIcon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: gray[500],
      color: 'white',
      position: 'absolute',
      right: '0',
      bottom: '8px',
      fontSize: '11px',
      width: '24px',
      height: '24px',
      cursor: 'pointer',
      borderRadius: '50%'
    },
    sideDrawerContentArea: {
      // height: 'calc(100vh - 186px)',
      // height: `${window.innerHeight - 170}px`,
      overflowY: 'auto',
      color: gray[300],
      flexGrow: '1',
      padding: '8px'
    },
    sideDrawerUserCardAvatar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '80px',
      height: '80px',
      color: 'white',
      borderRadius: '4px',
      position: 'relative'
    },
    sideDrawerUserCardAvatarImg: {
      width: 'auto',
      height: '80px',
      maxHeight: '80px'
    },
    sideDrawerAvatarCamera: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: gray[500],
      color: 'white',
      position: 'absolute',
      left: '-3px',
      top: '58px',
      fontSize: '11px',
      width: '24px',
      height: '24px',
      cursor: 'pointer',
      borderRadius: '50%'
    },
    sideDrawerUserCardText: {
      fontSize: '12px'
    },
    sideDrawerFooter: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      flexShrink: '0',
      position: 'absolute',
      bottom: '0',
      background: gray[700],
      height: '56px',
      width: '100%',
      borderTop: `1px solid ${gray[500]}`,
      paddingRight: '8px'
    },
    sectionHeading: {
      color: blue[200],
      fontSize: '14px',
      fontWeight: 'bold'
    },
    ssnSeparator: {
      padding: '16px 4px 0 4px'
    },
    drawerBannerCameraIcon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: gray[500],
      color: 'white',
      position: 'absolute',
      right: '4px',
      bottom: '8px',
      fontSize: '11px',
      width: '24px',
      height: '24px',
      cursor: 'pointer',
      borderRadius: '50%'
    },
    errMsg: {
      color: '#FF6060',
      fontSize: '12px'
    },
    menuItemIconContainerSmall: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '25px',
      width: '25px'
    },
    menuItemIconContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '40px',
      width: '40px'
    },
    menuItemIconSmall: {
      fontSize: '14px'
    },
    menuItemIcon: {
      fontSize: '20px'
    },
    criticalSmallText: {
      fontSize: '12px',
      color: salmon[500],
      paddingLeft: '8px',
      fontWeight: 'bold',
      width: '140px'
    },
    popupMenuItemSmall: {
      height: '25px',
      display: 'flex',
      fontSize: '14px',
      alignItems: 'center',
      cursor: 'pointer',
      color: '#5080B0',
      ':not(.selected):hover': {
        backgroundColor: '#0066CC',
        color: 'white'
      }
    },
    selectedPopupMenuItemSmall: {
      backgroundColor: '#0066CC',
      color: 'white'
    },
    imageUploadLoader: {
      height: 'calc(100% - 56px)'
    }
  });

  // ------------------- Event Handler -------------------
  // #region Handlers

  // Upload Cropped File
  const uploadCroppedImage = (blob) => {
    const headers = new Headers();
    const token = localStorage.getItem('token');
    headers.append('Authorization', `Bearer ${token}`);

    const formData = new FormData();
    formData.append('file', blob);
    const formDataKeys = Object.keys(imageUploadFormData);
    if (formDataKeys.length > 0) {
      // formData.append('patientId', patientId);
      for (let i = 0; i < formDataKeys.length; i++) {
        formData.append(formDataKeys[i], imageUploadFormData[formDataKeys[i]]);
      }
    }
    const requestOptions = { method: 'POST', headers, body: formData, redirect: 'follow' };

    fetch(`${BASE_URL}/${croppedFileUploadUrl}`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        // console.log(res);
        if (res.success) {
          console.log('Cropped image url', res.fileUrl);
          // props.reloadPatientDataData(res.usr);
          setImageUrl(res.fileUrl);
          onCroppedImageUpload(res.fileUrl);
          onClose(res.fileUrl);
        }
      })
      .catch(error => console.log('error', error));
  };

  // Upload 'dropped' file image
  const handleOnFileDrop = (img) => {
    const formData = new FormData();
    formData.append('file', img);
    const options = { method: 'POST', body: formData, redirect: 'follow' };
    setIsImageUploading(true);
    fetch(`${BASE_URL}/${rawFileUploadUrl}`, options)
      .then(response => {
        console.log('Raw image file dropped is uploaded!');
        console.log(response);
        return response.text();
      })
      .then(result => {
        const res = JSON.parse(result);
        console.log(res);
        if (res.success) {
          setImageUrl(res.fileUrl);
          setIsImageUploading(false);
          setShowCropper(true);
        }
      })
      .catch(error => console.log('error', error));
  };

  // Upload raw selected image from file input
  const uploadSelectedImage = () => {
    const formData = new FormData();
    const img = fileInputRef.current.files[0];
    formData.append('file', img);
    const options = { method: 'POST', body: formData, redirect: 'follow' };

    setIsImageUploading(true);
    fetch(`${BASE_URL}/${rawFileUploadUrl}`, options)
      .then(response => {
        console.log('Raw image file selected is uploaded!');
        console.log(response);
        return response.text();
      })
      .then(result => {
        const res = JSON.parse(result);
        console.log(res);
        if (res.success) {
          setImageUrl(res.fileUrl);
          setIsImageUploading(false);
          setShowCropper(true);
        }
      })
      .catch(error => console.log('error', error));
  };

  // Upload 'selected' file image
  const handleOnFileSelected = () => {
    if (fileInputRef.current.files.length > 0) {
      console.log('Selected file = ', fileInputRef.current.files[0]);
      console.log('typeof img', typeof fileInputRef.current.files[0]);
      setIsImageUploading(true);
      uploadSelectedImage();
    } else {
      console.log('No file is selected!');
    }
  };

  const handleOnClickCrop = () => {
    const imageElement = cropperRef.current;
    const { cropper } = imageElement;
    cropper.getCroppedCanvas().toBlob((blob) => {
      // const url = URL.createObjectURL(blob);
      // drawerAvatarRef.current.src = url;
      uploadCroppedImage(blob);
      // onClose();
    });
  };

  // #endregion

  return (
    <Drawer
      placement={placement}
      style={{ ...style }}
      visible={isVisible}
      toggleDrawer={onClose}
      zIndex={`${zIndex}`}
      showMask={false}
      width={window.innerWidth <= 700 ? window.innerWidth : 375}
    >
      <div className={css(styles.sideDrawerContainer)}>
        {/* Header */}
        <div className={css(styles.sideDrawerHeader)}>
          <div>{headerText}</div>
          <i
            className={`fa fa-times-circle ${css(styles.sideDrawerHeaderCloseIcon)}`}
            onClick={onClose}
          />
        </div>
        {/* Content Area */}
        <div className={css(styles.sideDrawerContentArea, styles.imageUploaderContainer)}>
          {showCropper
            ? <div>
              <Cropper
                src={imageUrl}
                style={{ height: 400, width: '100%' }}
                checkCrossOrigin
                checkOrientation={false}
                aspectRatio={cropperAspectRatio}
                background={false}
                viewMode={1}
                autoCropArea={1}
                guides={false}
                ref={cropperRef}
              />
            </div>
            : null
          }
          {!showCropper && !isImageUploading
            ? <div>
              <FileUploader
                title='Select File'
                parentRef={fileInputRef}
                onDrop={handleOnFileDrop}
                onChange={handleOnFileSelected}
              />
            </div>
            : null
          }
          {isImageUploading
            ? <div className={css(styles.imageUploadLoader)}>
              <Loader iconType="cog" iconSize="5x" text={uploadUserMessage} />
            </div>
            : null
          }
        </div>
        {/* Footer */}
        <div
          className={`fx fx__hv-center ${css(styles.sideDrawerFooter)}`}
          style={{ position: 'absolute', bottom: '0' }}
        >
          <Button
            btnType="primary"
            className="fx fx__hv-center"
            onClick={handleOnClickCrop}
            style={{
              height: '32px',
              width: '340px',
              padding: '3px 0'
            }}
          >
            {cropButtonText}
          </Button>
        </div>
      </div>
    </Drawer>
  );
};

export default CropperDrawer;
